/* globals fetchTicketingSummaryUrl */

import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';

import { formatAndRoundMoney, formatFigure } from 'Helpers/formatService';

import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

const TicketingSummaryTable = ({ fetchTicketingSummaryUrl }) => {
    const [sportsTicketingSummary, setSportsTicketingSummaryData] = useState([]);
    const [totals, setTotals] = useState({});
    const [expendedColumn, setExpendedColumn] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTicketingSummary();
    }, []);

    const fetchTicketingSummary = () => {
        setLoading(true);

        axios
            .get(fetchTicketingSummaryUrl)
            .then(({ data }) => {
                setSportsTicketingSummaryData(data.sportsTicketingSummary);
                setTotals(data.totals);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const toggleExpendedColumn = (columnIndex) => {
        setExpendedColumn((state) => {
            state[columnIndex] = !state[columnIndex];
            return { ...state };
        });
    };

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table inbud-table_first-sticky">
                <thead>
                    <tr>
                        <th className="tw-min-w-56 tw-max-w-56"></th>
                        <th className="tw-min-w-36 tw-border-r-2">{$t('Total')}</th>
                        {!loading &&
                            sportsTicketingSummary.map(({ name, venues }, index) =>
                                venues.length ? (
                                    <Fragment key={index}>
                                        <th
                                            className={classNames('tw-min-w-36 tw-cursor-pointer', { 'tw-border-r-2': !expendedColumn[index] })}
                                            onClick={() => toggleExpendedColumn(index)}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-x-2 tw-justify-between">
                                                <div className="tw-flex tw-justify-center tw-flex-grow">{name}</div>
                                                <div>{expendedColumn[index] ? <ChevronLeftIcon /> : <ChevronRightIcon />}</div>
                                            </div>
                                        </th>
                                        {expendedColumn[index] &&
                                            venues.map(({ name }, subIndex) => (
                                                <th
                                                    key={subIndex}
                                                    className={classNames('tw-min-w-36', {
                                                        'tw-border-r-2': expendedColumn[index] && subIndex == venues.length - 1,
                                                    })}
                                                >
                                                    {name}
                                                </th>
                                            ))}
                                    </Fragment>
                                ) : (
                                    <th key={index} className="tw-min-w-36 tw-border-r-2">
                                        {name}
                                    </th>
                                ),
                            )}
                    </tr>
                </thead>
                <tbody>
                    <TicketingSummaryTableRow
                        label={$t('Number of sessions')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="sessions"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Total seats available')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="capacity"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Total kills and holds')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="kills"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Total tickets available for sale')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="sellable"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Forecast number of tickets sold')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="sold"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('% of ticketing available for sale')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="sales"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Total Revenue')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="revenue"
                        loading={loading}
                        expendedColumn={expendedColumn}
                        isRevenue
                    />
                    <TicketingSummaryTableRow
                        label={$t('Highest price')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="highest"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Lowest price')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="lowest"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                    <TicketingSummaryTableRow
                        label={$t('Average Price')}
                        sportsTicketingSummary={sportsTicketingSummary}
                        totals={totals}
                        attribute="average"
                        loading={loading}
                        expendedColumn={expendedColumn}
                    />
                </tbody>
            </table>
        </div>
    );
};

const TicketingSummaryTableRow = ({ sportsTicketingSummary, totals, attribute, loading, label, isRevenue, expendedColumn }) => {
    return (
        <tr>
            <td className="!tw-bg-white">{label}</td>
            <td className={classNames('tw-font-semibold', 'inbud-numeric-cell', { '!tw-bg-blue tw-text-white': isRevenue })}>
                {!loading && formatFigure(totals[attribute], formatAndRoundMoney)}
            </td>
            {!loading &&
                sportsTicketingSummary.map(({ data, venues }, index) => (
                    <Fragment key={index}>
                        <td className={classNames('tw-font-semibold', 'inbud-numeric-cell', { '!tw-bg-blue tw-text-white': isRevenue })}>
                            {formatFigure(data[attribute], formatAndRoundMoney)}
                        </td>
                        {expendedColumn[index] &&
                            venues.map(({ data }, index) => (
                                <td key={index} className={classNames('inbud-numeric-cell', isRevenue ? '!tw-bg-charcoal tw-text-white' : '!tw-bg-white')}>
                                    {formatFigure(data[attribute], formatAndRoundMoney)}
                                </td>
                            ))}
                    </Fragment>
                ))}
        </tr>
    );
};

export default TicketingSummaryTable;

if (document.getElementById('TicketingSummaryTable')) {
    var rootEl = document.getElementById('TicketingSummaryTable');
    ReactDOM.render(
        <App>
            <TicketingSummaryTable fetchTicketingSummaryUrl={fetchTicketingSummaryUrl} />
        </App>,
        rootEl,
    );
}
